import React, { useState, useEffect } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { m } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from 'react-i18next';
import { useTranslatedQuery } from "../../hooks/useTranslatedContent";
import { fadeIn, fadeInLeft } from "../../Functions/GlobalAnimations";
import Accordion from "../../Components/Accordion/Accordion";
import FooterSection from "../Footer/FooterSection";
import HeaderSection from "../Header/HeaderSection";
import { getAllDocuments } from "../../firebase/dbService";
import { db, storage } from "../../config/firebase";
import { collection, getDocs } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import { getFileURL } from "../../firebase/storageService";
import 'react-toastify/dist/ReactToastify.css';
import Preloader from "../../Components/Preloader";

const fetchHeaderData = async () => {
  const headerSnapshot = await getAllDocuments('importantInformationHeader');
  if (headerSnapshot.docs.length > 0) {
    const headerData = headerSnapshot.docs[0].data();
    return {
      title: headerData.title,
      description: headerData.description,
    };
  }
  return null;
};

const fetchBodyData = async () => {
  const bodySnapshot = await getAllDocuments('importantInformation');
  return bodySnapshot.docs.map((doc) => ({
    title: doc.data().title,
    content: doc.data().description,
  }));
};

const getImageUrl = async (imagePath) => {
  try {
    const imageRef = ref(storage, imagePath);
    const url = await getDownloadURL(imageRef);
    return url;
  } catch (error) {
    console.error("Error fetching image URL:", error);
    return null;
  }
};

const fetchAboutUs = async () => {
  const querySnapshot = await getDocs(collection(db, "about"));
  if (!querySnapshot.empty) {
    const aboutData = querySnapshot.docs[0].data();
    const image = await getImageUrl(aboutData.image);
    return {
      title: aboutData.title,
      description: aboutData.description,
      image: image,
      imageTitle: aboutData.imageTitle,
      imageSubtitle: aboutData.imageSubtitle,
      feature1: aboutData.feature1 || "Professional event management services",
      feature2: aboutData.feature2 || "Experienced team of event coordinators",
      feature3: aboutData.feature3 || "Comprehensive event planning solutions",
    };
  }
  return {
    title: "Welcome to Pambazuka Events Management!",
    description: "We provide comprehensive event management services...",
    image: null,
    imageTitle: "Default Image Title",
    imageSubtitle: "Default Image Subtitle",
    feature1: "Professional event management services",
    feature2: "Experienced team of event coordinators",
    feature3: "Comprehensive event planning solutions",
  };
};

const fetchServiceData = async () => {
  const headerSnapshot = await getDocs(collection(db, "partnersHeader"));
  const bodySnapshot = await getDocs(collection(db, "partners"));

  const header = !headerSnapshot.empty ? headerSnapshot.docs[0].data().title : "Event Partners & Sponsors";

  // Load partner logos with error handling
  const body = await Promise.all(
    bodySnapshot.docs.map(async (doc) => {
      const data = doc.data();
      let logoUrl = null;

      // Try to load partner logo if it exists
      if (data.imageUrl) {
        try {
          logoUrl = await getFileURL(data.imageUrl);
        } catch (error) {
          console.error(`Error loading partner logo for ${data.name}:`, error);
        }
      }

      return {
        title: data.name,
        logo: logoUrl
      };
    })
  );

  return { header, body };
};

// Partners Carousel Component
const PartnersCarousel = ({ partners }) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const partnersPerPage = 12; // 6 per row x 2 rows

  useEffect(() => {
    if (partners.length <= partnersPerPage) return; // No need to cycle if all fit

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        const nextIndex = prevIndex + partnersPerPage;
        return nextIndex >= partners.length ? 0 : nextIndex;
      });
    }, 4000); // Change every 4 seconds

    return () => clearInterval(interval);
  }, [partners.length, partnersPerPage]);

  if (!partners || partners.length === 0) {
    return (
      <div className="text-center text-gray-500">
        {t('wichtigeInfo.noPartners')}
      </div>
    );
  }

  // Create chunks of partners for display
  const getVisiblePartners = () => {
    if (partners.length <= partnersPerPage) {
      return partners;
    }

    const visible = [];
    for (let i = 0; i < partnersPerPage; i++) {
      const index = (currentIndex + i) % partners.length;
      visible.push(partners[index]);
    }
    return visible;
  };

  const visiblePartners = getVisiblePartners();

  return (
    <div className="partners-carousel-container">
      {/* First Row - 6 Partners */}
      <div className="flex justify-center mb-8">
        <div className="grid grid-cols-6 gap-6 max-w-6xl w-full">
          {visiblePartners.slice(0, 6).map((partner, index) => (
            <m.div
              key={`${currentIndex}-${index}`}
              className="partner-item flex flex-col items-center"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="partner-logo-container mb-3 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-4 w-28 h-28 flex items-center justify-center" style={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                {partner.logo ? (
                  <img
                    src={partner.logo}
                    alt={partner.title}
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div
                  className={`w-16 h-16 rounded-lg flex items-center justify-center text-white font-bold text-xl ${partner.logo ? 'hidden' : 'flex'}`}
                  style={{ display: partner.logo ? 'none' : 'flex', backgroundColor: '#D4A853' }}
                >
                  {partner.title.charAt(0).toUpperCase()}
                </div>
              </div>
              <p className="text-center text-xs font-normal text-gray-600 capitalize leading-tight max-w-full" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
                {partner.title}
              </p>
            </m.div>
          ))}
        </div>
      </div>

      {/* Second Row - Next 6 Partners */}
      {visiblePartners.length > 6 && (
        <div className="flex justify-center">
          <div className="grid grid-cols-6 gap-6 max-w-6xl w-full">
            {visiblePartners.slice(6, 12).map((partner, index) => (
              <m.div
                key={`${currentIndex}-${index + 6}`}
                className="partner-item flex flex-col items-center"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: (index + 6) * 0.1 }}
              >
                <div className="partner-logo-container mb-3 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-4 w-28 h-28 flex items-center justify-center" style={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                  {partner.logo ? (
                    <img
                      src={partner.logo}
                      alt={partner.title}
                      className="max-w-full max-h-full object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div
                    className={`w-16 h-16 rounded-lg flex items-center justify-center text-white font-bold text-xl ${partner.logo ? 'hidden' : 'flex'}`}
                    style={{ display: partner.logo ? 'none' : 'flex', backgroundColor: '#D4A853' }}
                  >
                    {partner.title.charAt(0).toUpperCase()}
                  </div>
                </div>
                <p className="text-center text-xs font-normal text-gray-600 capitalize leading-tight max-w-full" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
                  {partner.title}
                </p>
              </m.div>
            ))}
          </div>
        </div>
      )}

      {/* Carousel Indicators */}
      {partners.length > partnersPerPage && (
        <div className="flex justify-center mt-8 space-x-2">
          {Array.from({ length: Math.ceil(partners.length / partnersPerPage) }).map((_, index) => (
            <button
              key={index}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                Math.floor(currentIndex / partnersPerPage) === index
                  ? 'scale-125'
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
              style={Math.floor(currentIndex / partnersPerPage) === index ? { backgroundColor: '#D4A853' } : {}}
              onClick={() => setCurrentIndex(index * partnersPerPage)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const WichtigeInfo = () => {
  const { data: headerData, isLoading: headerLoading, error: headerError } = useTranslatedQuery(
    ['headerData'], fetchHeaderData, ['title', 'description']
  );
  const { data: bodyData, isLoading: bodyLoading, error: bodyError } = useTranslatedQuery(
    ['bodyData'], fetchBodyData, ['title', 'content']
  );
  const { data: aboutUs, isLoading: aboutLoading, error: aboutError } = useTranslatedQuery(
    ['aboutUs'], fetchAboutUs, ['title', 'description', 'imageTitle', 'imageSubtitle', 'feature1', 'feature2', 'feature3']
  );
  const { data: serviceData, isLoading: serviceLoading, error: serviceError } = useQuery({
    queryKey: ['serviceData'],
    queryFn: fetchServiceData,
  });
  const { t } = useTranslation();

  if (headerLoading || bodyLoading || aboutLoading || serviceLoading) {
    return <Preloader />;
  }

  if (headerError || bodyError || aboutError || serviceError) {
    return <div>{t('common.errorLoading')}</div>;
  }

  return (
    <>
      {/* Header Start */}
      <HeaderSection theme="light" />
      {/* Header End */}

      {/* Modern Hero Header */}
      {headerData && (
        <section className="relative py-[120px] lg:py-[100px] md:py-[80px] sm:py-[60px] mt-[100px] overflow-hidden" style={{ backgroundColor: '#FFFBF5' }}>
          <Container className="relative z-10">
            <Row className="justify-center">
              <Col lg={10} md={12} className="text-center">
                <m.div {...fadeIn}>
                  {/* Subtitle */}
                  <div className="inline-flex items-center justify-center mb-[30px]">
                    <div className="w-[50px] h-[1px] mr-4" style={{ backgroundColor: '#D4A853' }}></div>
                    <span className="uppercase tracking-[3px] text-sm font-medium" style={{ color: '#D4A853', fontFamily: "'Inter', system-ui, sans-serif", fontSize: '13px', letterSpacing: '2px' }}>{t('wichtigeInfo.welcome')}</span>
                    <div className="w-[50px] h-[1px] ml-4" style={{ backgroundColor: '#D4A853' }}></div>
                  </div>

                  {/* Main Title */}
                  <h1 className="heading-1 font-bold mb-[40px] leading-tight" style={{ color: '#1a1a2e', fontFamily: "'Inter', system-ui, sans-serif", fontSize: '2rem', fontWeight: 700 }}>
                    {headerData.title}
                  </h1>

                  {/* Description Card */}
                  <div className="max-w-4xl mx-auto">
                    <div className="bg-white p-[50px] lg:p-[40px] md:p-[35px] sm:p-[25px]" style={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                      <div className="text-xl lg:text-lg md:text-base leading-relaxed text-gray-700 font-light"
                           style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
                           dangerouslySetInnerHTML={{ __html: headerData.description }}>
                      </div>

                      {/* Cultural Quote */}
                      <div className="mt-[40px] pt-[30px] border-t border-gray-200">
                        <blockquote className="text-lg italic mb-[15px]" style={{ color: '#D4A853', fontFamily: "'Inter', system-ui, sans-serif" }}>
                          {t('wichtigeInfo.culturalQuote')}
                        </blockquote>
                        <p className="text-sm text-gray-600 font-medium" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
                          {t('wichtigeInfo.culturalQuoteTranslation')}
                        </p>
                      </div>
                    </div>
                  </div>
                </m.div>
              </Col>
            </Row>
          </Container>
        </section>
      )}

      {/* Enhanced Accordion Section */}
      {bodyData && bodyData.length > 0 && (
        <m.section id="about-us-info" className="bg-white py-[100px] lg:py-[80px] md:py-[60px] sm:py-[40px]">
          <Container>
            <Row className="justify-center">
              <Col lg={10} md={12}>
                <div className="text-center mb-[60px]">
                  <h2 className="heading-3 font-semibold mb-[20px]" style={{ color: '#1a1a2e', fontFamily: "'Inter', system-ui, sans-serif", fontSize: '2rem', fontWeight: 700 }}>{t('wichtigeInfo.importantInfo')}</h2>
                  <div className="w-[80px] h-[3px] mx-auto rounded-full" style={{ backgroundColor: '#D4A853' }}></div>
                </div>
                <div className="p-[40px] lg:p-[35px] md:p-[30px]" style={{ backgroundColor: '#FFFBF5', borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                  <Accordion theme="accordion-style-03" animation={fadeIn} data={bodyData} />
                </div>
              </Col>
            </Row>
          </Container>
        </m.section>
      )}

      {/* Modern About Us Section */}
      {aboutUs && (
        <m.section
          className="py-[120px] lg:py-[100px] md:py-[80px] sm:py-[60px]"
          style={{ backgroundColor: '#FFFBF5' }}
          {...fadeIn}
        >
          <Container>
            {/* Section Header */}
            <Row className="justify-center mb-[80px] lg:mb-[60px] md:mb-[50px]">
              <Col lg={8} md={10} className="text-center">
                <m.div {...fadeIn}>
                  <div className="inline-flex items-center justify-center mb-[25px]">
                    <div className="w-[60px] h-[2px] mr-4" style={{ backgroundColor: '#D4A853' }}></div>
                    <span className="uppercase tracking-[2px] text-sm font-medium" style={{ color: '#D4A853', fontFamily: "'Inter', system-ui, sans-serif", fontSize: '13px', letterSpacing: '2px' }}>{t('wichtigeInfo.aboutUs')}</span>
                    <div className="w-[60px] h-[2px] ml-4" style={{ backgroundColor: '#D4A853' }}></div>
                  </div>
                  <div className="text-lg leading-relaxed text-gray-600 max-w-3xl mx-auto"
                       style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
                       dangerouslySetInnerHTML={{ __html: aboutUs.description }}>
                  </div>
                </m.div>
              </Col>
            </Row>

            {/* Image and Content Grid */}
            {aboutUs.image && (
              <Row className="items-center mb-[80px] lg:mb-[60px]">
                <Col lg={6} md={12} className="mb-[40px] lg:mb-0">
                  <m.div className="relative" {...fadeInLeft}>
                    <div className="relative overflow-hidden shadow-2xl" style={{ borderRadius: '16px' }}>
                      <img
                        className="w-full h-[400px] lg:h-[500px] object-cover transition-transform duration-700 hover:scale-105"
                        src={aboutUs.image}
                        alt="About Us"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                    </div>
                  </m.div>
                </Col>
                <Col lg={6} md={12}>
                  <m.div className="lg:pl-[60px] md:pl-[40px]" {...{ ...fadeIn, transition: { delay: 0.3 } }}>
                    {aboutUs.imageTitle && (
                      <h3 className="heading-4 font-semibold mb-[25px]" style={{ color: '#1a1a2e', fontFamily: "'Inter', system-ui, sans-serif" }}>
                        {aboutUs.imageTitle}
                      </h3>
                    )}
                    {aboutUs.imageSubtitle && (
                      <p className="text-lg text-gray-600 leading-relaxed mb-[30px]" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
                        {aboutUs.imageSubtitle}
                      </p>
                    )}

                    {/* Features List */}
                    <div className="space-y-[20px]">
                      {[aboutUs.feature1, aboutUs.feature2, aboutUs.feature3].filter(Boolean).map((feature, idx) => (
                        <div key={idx} className="flex items-start">
                          <div className="w-[8px] h-[8px] rounded-full mt-[8px] mr-[15px] flex-shrink-0" style={{ backgroundColor: '#D4A853' }}></div>
                          <p className="text-gray-600 leading-relaxed" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>{feature}</p>
                        </div>
                      ))}
                    </div>
                  </m.div>
                </Col>
              </Row>
            )}

          </Container>
        </m.section>
      )}

      {/* Partners Section */}
      {serviceData && serviceData.body && serviceData.body.length > 0 && (
        <section className="bg-white py-[160px] border-t lg:py-[120px] md:py-[95px] sm:py-[80px] xs:py-[50px]">
          <Container>
            <Row>
              <Col className="mb-[60px]">
                <h6 className="text-center font-medium mb-[25px] lg:mb-[15px]" style={{ color: '#1a1a2e', fontFamily: "'Inter', system-ui, sans-serif" }}>
                  {serviceData.header}
                </h6>
              </Col>
            </Row>
            <PartnersCarousel partners={serviceData.body} />
          </Container>
        </section>
      )}

      {/* Footer */}
      <FooterSection />
    </>
  );
};

export default WichtigeInfo;
